import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { API, Auth, graphqlOperation } from "aws-amplify";
import Sidebar from "./components/Sidebar";
import LogoutPage from "./pages/LogoutPage";
import LoginPage from "./pages/LoginPage";
import LineupSharePage from "./pages/LineupSharePage";
import { getUser, listLeagues, listOrganizations, listPlayerParents, listTeams, listUserAssignments } from "./graphql/queries";
import { createUser, updateUser } from "./graphql/mutations";
import { adminRouteCatalog } from "./app/adminRouteCatalog";
import { hasAnyRole } from "./lib/roles";
import { buildRoleScopes, type ActiveRoleScope } from "./lib/roleContext";

type AuthStatus = {
  isAuthenticated: boolean;
  userRoles: string[];
  username: string;
  email: string;
};

type ProfileDraft = {
  username: string;
  email: string;
  givenName: string;
  familyName: string;
  phone: string;
  safetyCertified: boolean;
  abcaId: string;
};

const App: React.FC = () => {
  const [authStatus, setAuthStatus] = useState<AuthStatus>({ isAuthenticated: false, userRoles: [], username: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [roleScopes, setRoleScopes] = useState<ActiveRoleScope[]>([]);
  const [activeRoleScopeId, setActiveRoleScopeId] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem("legendsField.sidebarCollapsed") === "true";
    } catch {
      return false;
    }
  });
  const [profileRequired, setProfileRequired] = useState(false);
  const [checkingProfile, setCheckingProfile] = useState(false);
  const [profileDraft, setProfileDraft] = useState<ProfileDraft>({
    username: "",
    email: "",
    givenName: "",
    familyName: "",
    phone: "",
    safetyCertified: false,
    abcaId: ""
  });
  const [profileError, setProfileError] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  const normalizeRoleName = (role: string): string => {
    const trimmed = role.trim();
    if (!trimmed) {
      return "";
    }
    if (trimmed.toLowerCase() === "parents") {
      return "Parent";
    }
    return trimmed;
  };

  const normalizeRoles = (groups: unknown): string[] => {
    const toNormalized = (list: string[]) =>
      Array.from(new Set(["Parent", ...list.map((value) => normalizeRoleName(value)).filter(Boolean)]));
    if (Array.isArray(groups)) {
      return toNormalized(groups.filter((group): group is string => typeof group === "string"));
    }
    if (typeof groups === "string") {
      return toNormalized(groups.split(","));
    }
    return ["Parent"];
  };

  const ensureUserProfile = useCallback(async (user: any, roles: string[]) => {
    const username = user?.username ?? "";
    if (!username) {
      setProfileRequired(false);
      return;
    }

    setCheckingProfile(true);
    setProfileError("");
    try {
      const profileRes: any = await API.graphql(graphqlOperation(getUser, { id: username }));
      const existingProfile = profileRes?.data?.getUser;
      if (existingProfile) {
        setProfileRequired(false);
        return;
      }

      const attrs = user?.attributes ?? {};
      setProfileDraft({
        username,
        email: attrs.email ?? "",
        givenName: attrs.given_name ?? "",
        familyName: attrs.family_name ?? "",
        phone: attrs.phone_number ?? "",
        safetyCertified: false,
        abcaId: ""
      });
      setProfileRequired(true);
    } catch {
      const attrs = user?.attributes ?? {};
      setProfileDraft({
        username,
        email: attrs.email ?? "",
        givenName: attrs.given_name ?? "",
        familyName: attrs.family_name ?? "",
        phone: attrs.phone_number ?? "",
        safetyCertified: false,
        abcaId: ""
      });
      setProfileRequired(true);
    } finally {
      setCheckingProfile(false);
    }
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const groups = user.signInUserSession?.idToken?.payload["cognito:groups"];
      const roles = normalizeRoles(groups);
      const username = user?.username ?? "";
      const email = user?.attributes?.email ?? "";

      setAuthStatus({ isAuthenticated: true, userRoles: roles, username, email });
      await ensureUserProfile(user, roles);
    } catch {
      setAuthStatus({ isAuthenticated: false, userRoles: [], username: "", email: "" });
      setRoleScopes([]);
      setActiveRoleScopeId("");
      setProfileRequired(false);
    } finally {
      setLoading(false);
    }
  }, [ensureUserProfile]);

  const loadRoleScopes = useCallback(async () => {
    if (!authStatus.isAuthenticated || !authStatus.username) {
      setRoleScopes([]);
      setActiveRoleScopeId("");
      return;
    }

    try {
      const [orgRes, leagueRes, teamRes, assignmentRes, parentRes] = await Promise.allSettled([
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 })),
        API.graphql(graphqlOperation(listPlayerParents, { limit: 5000 }))
      ]);

      const organizations =
        orgRes.status === "fulfilled" ? (((orgRes.value as any)?.data?.listOrganizations?.items ?? []).filter(Boolean) as any[]) : [];
      const leagues =
        leagueRes.status === "fulfilled" ? (((leagueRes.value as any)?.data?.listLeagues?.items ?? []).filter(Boolean) as any[]) : [];
      const teams =
        teamRes.status === "fulfilled" ? (((teamRes.value as any)?.data?.listTeams?.items ?? []).filter(Boolean) as any[]) : [];
      const userAssignments =
        assignmentRes.status === "fulfilled"
          ? (((assignmentRes.value as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean) as any[])
          : [];
      const playerParents =
        parentRes.status === "fulfilled" ? (((parentRes.value as any)?.data?.listPlayerParents?.items ?? []).filter(Boolean) as any[]) : [];

      const nextScopes = buildRoleScopes({
        userRoles: authStatus.userRoles,
        username: authStatus.username,
        email: authStatus.email,
        organizations,
        leagues,
        teams,
        userAssignments,
        playerParents
      });

      setRoleScopes(nextScopes);
      setActiveRoleScopeId((current) => {
        const storageKey = `legendsField.activeRoleScopeId.${authStatus.username}`;
        let saved = "";
        try {
          saved = window.localStorage.getItem(storageKey) ?? "";
        } catch {
          saved = "";
        }
        const preferred = current || saved;
        if (preferred && nextScopes.some((scope) => scope.id === preferred)) {
          return preferred;
        }
        return nextScopes[0]?.id ?? "";
      });
    } catch {
      const fallbackScopes = buildRoleScopes({
        userRoles: authStatus.userRoles,
        username: authStatus.username,
        email: authStatus.email
      });
      setRoleScopes(fallbackScopes);
      setActiveRoleScopeId(fallbackScopes[0]?.id ?? "");
    }
  }, [authStatus.email, authStatus.isAuthenticated, authStatus.userRoles, authStatus.username]);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    loadRoleScopes();
  }, [loadRoleScopes]);

  useEffect(() => {
    try {
      window.localStorage.setItem("legendsField.sidebarCollapsed", String(sidebarCollapsed));
    } catch {
      // Local storage can be unavailable in some embedded browsers.
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    const onFocus = () => {
      refreshAuth();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refreshAuth]);

  const onSignedOut = useCallback(() => {
    setAuthStatus({ isAuthenticated: false, userRoles: [], username: "", email: "" });
    setRoleScopes([]);
    setActiveRoleScopeId("");
    setProfileRequired(false);
  }, []);

  const activeRoleScope = useMemo(
    () => roleScopes.find((scope) => scope.id === activeRoleScopeId) ?? roleScopes[0] ?? null,
    [activeRoleScopeId, roleScopes]
  );

  const handleRoleScopeChange = useCallback(
    (scopeId: string) => {
      setActiveRoleScopeId(scopeId);
      try {
        if (authStatus.username) {
          window.localStorage.setItem(`legendsField.activeRoleScopeId.${authStatus.username}`, scopeId);
        }
      } catch {
        // Local storage can be unavailable in some embedded browsers.
      }
    },
    [authStatus.username]
  );

  const saveRequiredProfile = async () => {
    if (!profileDraft.username || !profileDraft.email) {
      setProfileError("Email is required to create your profile.");
      return;
    }

    setSavingProfile(true);
    setProfileError("");
    try {
      const roles = authStatus.userRoles.length > 0 ? authStatus.userRoles : ["Parent"];
      const input = {
        id: profileDraft.username,
        email: profileDraft.email,
        givenName: profileDraft.givenName || null,
        familyName: profileDraft.familyName || null,
        phone: profileDraft.phone || null,
        safetyCertified: profileDraft.safetyCertified,
        abcaId: profileDraft.abcaId || null,
        roles
      };
      try {
        await API.graphql(graphqlOperation(createUser, { input }));
      } catch (error: any) {
        const message = error?.errors?.[0]?.message ?? error?.message ?? "";
        if (String(message).toLowerCase().includes("already exists")) {
          await API.graphql(graphqlOperation(updateUser, { input }));
        } else {
          throw error;
        }
      }
      setProfileRequired(false);
    } catch (error: any) {
      const details =
        error?.errors?.map((item: any) => item?.message).filter(Boolean).join(" | ") ||
        error?.message ||
        "Failed to create profile.";
      setProfileError(details);
    } finally {
      setSavingProfile(false);
    }
  };

  const appContent = useMemo(() => {
    if (!authStatus.isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    return (
      <div className="app-shell">
        <Sidebar
          userRoles={authStatus.userRoles}
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
          roleScopes={roleScopes}
          activeRoleScope={activeRoleScope}
          onRoleScopeChange={handleRoleScopeChange}
        />
        <main className={`flex-1 p-6 app-main ${sidebarCollapsed ? "app-main--sidebar-collapsed" : ""}`}>
          <Routes>
            {adminRouteCatalog.map((routeDef) => (
              <Route
                key={routeDef.path}
                path={routeDef.path}
                element={
                  hasAnyRole(authStatus.userRoles, routeDef.allowedRoles)
                    ? routeDef.render(authStatus.userRoles, activeRoleScope)
                    : <Navigate to="dashboard" replace />
                }
              />
            ))}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </main>
      </div>
    );
  }, [activeRoleScope, authStatus.isAuthenticated, authStatus.userRoles, handleRoleScopeChange, roleScopes, sidebarCollapsed]);

  if (loading) {
    return <div className="p-6">Loading PitchTracker...</div>;
  }

  const profileModal = authStatus.isAuthenticated && profileRequired && !checkingProfile
    ? createPortal(
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 99999,
            display: "grid",
            placeItems: "center",
            background: "rgba(0, 0, 0, 0.52)",
            padding: "1rem"
          }}
        >
          <div
            className="w-full max-w-xl rounded-lg bg-white p-6 shadow-lg"
            style={{
              maxHeight: "90vh",
              overflowY: "auto"
            }}
          >
            <h2 className="text-xl font-bold mb-2">Complete Your Profile</h2>
            <p className="text-sm text-gray-600 mb-4">
              We need your profile details before you can use the app.
            </p>
            {profileError && <p className="mb-3 text-sm" style={{ color: "#c1121f" }}>{profileError}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              <input
                className="border px-3 py-2 rounded"
                placeholder="First name"
                value={profileDraft.givenName}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, givenName: event.target.value }))}
              />
              <input
                className="border px-3 py-2 rounded"
                placeholder="Last name"
                value={profileDraft.familyName}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, familyName: event.target.value }))}
              />
              <input
                className="border px-3 py-2 rounded md:col-span-2"
                placeholder="Email"
                value={profileDraft.email}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, email: event.target.value }))}
              />
              <input
                className="border px-3 py-2 rounded"
                placeholder="Phone"
                value={profileDraft.phone}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, phone: event.target.value }))}
              />
              <input
                className="border px-3 py-2 rounded"
                placeholder="ABCA ID"
                value={profileDraft.abcaId}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, abcaId: event.target.value }))}
              />
            </div>
            <label className="inline-flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={profileDraft.safetyCertified}
                onChange={(event) => setProfileDraft((prev) => ({ ...prev, safetyCertified: event.target.checked }))}
              />
              Safety Certified
            </label>
            <div>
              <button
                onClick={saveRequiredProfile}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                disabled={savingProfile}
              >
                {savingProfile ? "Saving..." : "Create Profile"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <Router>
      {profileModal}
      <Routes>
        <Route
          path="/login"
          element={authStatus.isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <LoginPage />}
        />
        <Route path="/lineup-share" element={<LineupSharePage />} />
        <Route path="/logout" element={<LogoutPage onSignedOut={onSignedOut} />} />
        <Route path="/admin/*" element={appContent} />
        <Route path="/" element={<Navigate to={authStatus.isAuthenticated ? "/admin/dashboard" : "/login"} replace />} />
        <Route path="*" element={<Navigate to={authStatus.isAuthenticated ? "/admin/dashboard" : "/login"} replace />} />
      </Routes>
    </Router>
  );
};

export default App;
